/*
 * Copyright (c) Nexign, JSC, 2020-2021.
 */
/**
 * Tariffs And Services Side Menu Component
 *
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Yakov Gushchin
 * @version 1.16.0
 * @since 1.4.0
 */

import { useCallback, useEffect, useState } from 'react';

import { Accordion } from 'react-accessible-accordion';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { ONBOARDING_DATA_ATTR } from '../../../modules/onboarding/onboardingConstants';
import { EOnboardingComponentsId } from '../../../modules/onboarding/types/onboardingTypes';
import { IRootReducer } from '../../../reducers/rootReducer';
import { productsSetCategorySelectedAction, productsSetFilterSelectionAction } from '../tariffsAndServicesActions';
import { EProductsCategory, EProductsTab } from '../tariffsAndServicesConstants';
import { TProductItem } from '../tariffsAndServicesProductTypes';
import {
    makeSelectProductsCategories,
    makeSelectProductsCategorySelected,
    makeSelectProductsFilterShowSelectedOnly,
    makeSelectProductsList,
    makeSelectProductsMeta,
    makeSelectProductsTabSelected,
    makeSelectTariffsAndServicesCategoryListSelected,
} from '../tariffsAndServicesSelectors';
import { IProductsCategory, IProductsMeta } from '../tariffsAndServicesTypes';
import ProductsSideMenuLevel from './ProductsSideMenuLevel';
import ProductsSideSearch from './ProductsSideSearch';
import { TariffsAndServicesSideMenuStyled } from './styled/TariffsAndServicesSideMenuStyled';

export type TProductsSideMenu = IConnectedProps & IDispatchProps;

export const ProductsSideMenu = (props: IConnectedProps & IDispatchProps) => {
    const {
        categorySelected,
        productsMeta,
        setCategorySelected,
    } = props;
    const [categoryList, setCategoryList] = useState<IProductsCategory[]>([]);

    useEffect(() => {
        const list = props.showSelectedOnly ? props.categoryListSelected : props.categoryList;
        setCategoryList(list.filter(cat => {
            const categoryWithNestedCategories = list.find(category => category.parent === cat.id);
            return !(props.typeSelected === EProductsTab.CONNECTED && cat.id === productsMeta.popularCategoryId)
                && props.productsList.find(
                    item =>
                        // the category is displayed if category contains products
                        item.categories.includes(cat.id)
                        // or the category has nested categories with products
                        || item.categories.includes(categoryWithNestedCategories?.id || 0),
                )
                // Service and selected categories should always be visible
                || cat.id === productsMeta.servicesCategoryId
                || cat.id === EProductsCategory.SELECTED;
        }));
    }, [props.showSelectedOnly, props.categoryList, props.categoryListSelected, props.productsList, productsMeta]);

    const isSelected: ((id: number) => boolean) = useCallback((id) => {
        return categorySelected === id;
    }, [categorySelected]);

    /**
     * Метод рекурсивно проверяет цепочку активных пунктов
     */
    const isParentSelected: ((id: number) => boolean) = useCallback((id) => {
        const children = props.categoryList.filter(item => item.parent === id);
        if (children.length) {
            let result = false;
            children.forEach(child => {
                if (isSelected(child.id) || isParentSelected(child.id)) {
                    result = true;
                }
            });
            return result;
        }
        return false;
    }, [props.categoryList, isSelected]);

    const preExpandedCategories = [productsMeta.servicesCategoryId.toString()];

    return <TariffsAndServicesSideMenuStyled>
        <div {...{ [ONBOARDING_DATA_ATTR]: EOnboardingComponentsId.productCategories }}>
            <ProductsSideSearch/>
            <Accordion allowMultipleExpanded={true} allowZeroExpanded={true}>
                <ProductsSideMenuLevel
                    categoryList={categoryList}
                    isParentSelected={isParentSelected}
                    isSelected={isSelected}
                    level={0}
                    parent={productsMeta.parentCategoryId}
                    preExpandedCategories={preExpandedCategories}
                    onClick={setCategorySelected}
                />
            </Accordion>
        </div>
    </TariffsAndServicesSideMenuStyled>;
};

interface IConnectedProps {
    categoryList: IProductsCategory[];
    categoryListSelected: IProductsCategory[];
    categorySelected: number;
    showSelectedOnly: boolean;
    productsList: TProductItem[];
    productsMeta: IProductsMeta;
    typeSelected: EProductsTab;
}

const mapStateToProps = createStructuredSelector<IRootReducer, IConnectedProps>({
    categoryList: makeSelectProductsCategories(),
    categoryListSelected: makeSelectTariffsAndServicesCategoryListSelected(),
    categorySelected: makeSelectProductsCategorySelected(),
    showSelectedOnly: makeSelectProductsFilterShowSelectedOnly(),
    productsList: makeSelectProductsList(),
    productsMeta: makeSelectProductsMeta(),
    typeSelected: makeSelectProductsTabSelected(),
});

interface IDispatchProps {
    setCategorySelected: (payload: number) => void;
    setShowSelectedOnly: (payload: boolean) => void;
}

export const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    setCategorySelected: (payload) => dispatch(productsSetCategorySelectedAction(payload)),
    setShowSelectedOnly: (payload: boolean) => dispatch(productsSetFilterSelectionAction(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductsSideMenu);
